import MobilePage from "../UiComponents/MobilePage"
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import butterflyApi from "../ButterflyApi";
import FileUpload from "../Utils/FileUpload";
import Utils from "../utils";
import { wait } from "../Utils/Wait";
import { logout } from '../Utils/Cookies.js';
import './Registration.css';

const AddHero = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [birthYear, setBirthYear] = useState()
  const [gender, setGender] = useState('')
  const [heroImage, setHeroImage] = useState('')
  const [emailAgree, setEmailAgree] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    const loadEmailAgree = async () => {
      try {
        const supporter = await butterflyApi.getSupporter()
        if (supporter && supporter.emailAgree !== undefined) {
          setEmailAgree(supporter.emailAgree)
        }
      } catch (error) {
        console.error('이메일 수신 동의 상태 로드 오류:', error)
      }
    }
    loadEmailAgree()
  }, [])

  const onEmailAgreeChange = async (e) => {
    const checked = e.target.checked
    setEmailAgree(checked)
    try {
      await butterflyApi.updateEmailAgree(checked)
    } catch (error) {
      alert('이메일 수신 동의 설정 중 오류가 발생했습니다.')
      setEmailAgree(!checked)
    }
  }

  const onLeave = async () => {
    if (!window.confirm('정말 탈퇴하시겠습니까?\n탈퇴하시면 모든 데이터가 삭제되며 복구할 수 없습니다.')) {
      return
    }
    try {
      await butterflyApi.leave()
      alert('탈퇴가 완료되었습니다.')
      logout()
      navigate('/')
    } catch (error) {
      alert('탈퇴 처리 중 오류가 발생했습니다.')
    }
  }

  const imageUpload = async (fileInfo) => {
    const url = fileInfo.operationUrls?.[0]?.replace("http://", "https://") || fileInfo.url.replace("http://", "https://")
    setLoadingMessage("이미지 처리 중에는\n 시간이 오래 걸릴 수 있습니다.\n잠시만 기다려 주세요.")
    setLoading(true)
    await wait(5000) // 5초간 기다리는 코드
    setLoading(false)
    setHeroImage(url)
  }

  const onAdd = async () => {
    setSubmitError('')
    setLoadingMessage("이미지 처리 중에는\n 시간이 오래 걸릴 수 있습니다.\n잠시만 기다려 주세요.")
    setLoading(true)
    try {
      const hero = await butterflyApi.addHero(
        firstName, lastName, heroImage, gender, birthYear,
      )
      if (!hero?.heroNo) {
        setSubmitError('주인공을 등록하지 못했어요. 잠시 후 다시 시도해 주세요.')
        return
      }
      localStorage.removeItem("heroes")
      // Registration already succeeded; a failed list refresh must not prompt a second registration.
      const heroesResponse = await butterflyApi.getHeroes().catch(() => null)
      const heroes = heroesResponse?.items?.length ? heroesResponse.items : [hero]
      localStorage.setItem("heroes", JSON.stringify(heroes))
      localStorage.setItem("heroNo", hero.heroNo)
      navigate(`/heroes/${hero.heroNo}/setting`)
    } catch (error) {
      setSubmitError('주인공을 등록하지 못했어요. 잠시 후 다시 시도해 주세요.')
    } finally {
      setLoading(false)
      setLoadingMessage('')
    }
  }

  return <MobilePage className='storybook-page registration-page' loading={loading} loadingMessage={loadingMessage}>
    <main className='registration-content'>
      <header className='registration-header'>
        <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 우리 아이의 첫 번째 이야기</p>
        <h1 className='book-title'>새로운 주인공을<br />소개해 주세요.</h1>
        <p className='book-description'>아이의 이름과 모습을 알려주세요.<br />우리 가족만의 동화가 여기서 시작돼요.</p>
        <img src='/image/main/books.png' alt='' width='56' height='56' />
      </header>

      <form className='registration-form' onSubmit={event => { event.preventDefault(); if (!loading) onAdd() }}>
        <section className='registration-card' aria-labelledby='photo-heading'>
          <div className='registration-section-heading'>
            <span aria-hidden='true'>01</span>
            <h2 id='photo-heading'>아이의 사진</h2>
          </div>
          <p className='registration-hint'>얼굴이 잘 보이는 독사진을 골라주세요.</p>
          <div className={'registration-photo' + (heroImage ? ' has-photo' : '')}>
            <img src={heroImage || '/image/no_profile_image.png'} alt={heroImage ? '등록할 아이의 사진' : '아이 사진 미리보기'} />
          </div>
          <FileUpload
            path='/public/images/temp'
            filename={Utils.uuidv4()}
            operations='300'
            uploaded={async fileInfo => { await imageUpload(fileInfo) }}
            maxSize={10000000}
            overSizeMessage='10MB 이하의 이미지만 업로드 가능합니다.'
            onlyImage={true}
          >
            <button className='book-secondary-button registration-upload' type='button' disabled={loading}>
              <img src='/image/photo.svg' alt='' width='20' height='20' />
              {heroImage ? '사진 바꾸기' : '사진 선택하기'}
            </button>
          </FileUpload>
          <p className='registration-file-hint'>JPG · PNG · GIF / 최대 10MB</p>
          <p className='registration-photo-note'>업로드한 사진은 동화 이미지로 변환 후 자동으로 삭제됩니다.</p>
        </section>

        <section className='registration-card' aria-labelledby='profile-heading'>
          <div className='registration-section-heading'>
            <span aria-hidden='true'>02</span>
            <h2 id='profile-heading'>아이에 대해 알려주세요</h2>
          </div>
          <div className='registration-fields registration-fields--name'>
            <label>성<input name='lastName' value={lastName} placeholder='김' autoComplete='off' onChange={event => setLastName(event.target.value)} required /></label>
            <label>이름<input name='firstName' value={firstName} placeholder='하늘' autoComplete='off' onChange={event => setFirstName(event.target.value)} required /></label>
          </div>
          <div className='registration-fields'>
            <label><span id='hero-birth-year-label'>태어난 해</span>
              <select aria-labelledby='hero-birth-year-label' name='birthYear' value={birthYear || ''} onChange={event => setBirthYear(event.target.value)} required>
                <option value=''>선택해 주세요</option>
                {Array.from({ length: 9 }, (_, i) => new Date().getFullYear() - 2 - i).map(year => <option key={year} value={year}>{year}년</option>)}
              </select>
            </label>
            <label><span id='hero-gender-label'>성별</span>
              <select aria-labelledby='hero-gender-label' name='gender' value={gender} onChange={event => setGender(event.target.value)} required>
                <option value=''>선택해 주세요</option>
                <option value='M'>남아</option>
                <option value='F'>여아</option>
              </select>
            </label>
          </div>
          <p className='registration-hint'>입력한 이름과 사진으로 동화 속 주인공이 만들어져요.</p>
        </section>
        {submitError && <p className='book-error' role='alert'>{submitError}</p>}
        <button type='submit' className='book-primary-button registration-submit' disabled={loading}>
          주인공 등록하기 <span aria-hidden='true'>→</span>
        </button>
        <p className='registration-next'>다음 화면에서 가족과 친구도 소개할 수 있어요.</p>
      </form>

      <footer className='registration-account'>
        <label className='registration-email'>
          <input type='checkbox' checked={emailAgree} onChange={onEmailAgreeChange} />
          <span>이메일로 소식 받기 <small>선택</small></span>
        </label>
        <div>
          <button className='book-text-link' type='button' onClick={() => { logout(); navigate('/') }}>로그아웃</button>
          <button className='registration-leave' type='button' onClick={onLeave}>탈퇴하기</button>
        </div>
      </footer>
    </main>
  </MobilePage>
}

export default AddHero
